import React, { useState } from 'react'

import { addressFormatter } from '@guiker/base-entity'
import { PointOfInterestContainer } from '@guiker/base-listing-components'
import { makeStyles } from '@guiker/components-core'
import { Flex, H4, Spinner } from '@guiker/components-library'
import { useTranslation } from '@guiker/i18n'
import { MapLocation, PointOfInterest, PointOfInterestType } from '@guiker/map-shared'
import { GoogleMap, Markers } from '@guiker/react-google-maps'
import { useQuery } from '@guiker/react-query'
import { RentalListing } from '@guiker/rental-listing-shared'

import { useMapApiClient } from '../../hooks'

type LocationProps = {
  listing: RentalListing
}

const useStyle = makeStyles(
  (theme) => ({
    mapContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      maxHeight: 500,
      maxWidth: theme.dimensions.narrowPageLayout.maxWidth.desktop,
      overflow: 'hidden',
    },
  }),
  {
    name: 'Location',
  },
)

const Location: React.FC<LocationProps> = ({ listing }) => {
  const classes = useStyle()
  const apiClient = useMapApiClient()
  const { t } = useTranslation(['main-rentalListing'])
  const [subwayList, setSubwayList] = useState<PointOfInterest[]>()
  const [universityList, setUniversityList] = useState<PointOfInterest[]>()
  const center = listing.address?.coordinates

  const { isLoading } = useQuery<MapLocation>(
    ['locate-poi', listing.id],
    () => apiClient.locatePointsOfInterest({ payload: center }),
    {
      onSuccess: (result) => {
        setSubwayList(result.pointsOfInterests.filter((poi) => poi.type === PointOfInterestType.SUBWAY_STATION))
        setUniversityList(result.pointsOfInterests.filter((poi) => poi.type === PointOfInterestType.UNIVERSITY))
      },
      enabled: !!listing,
      retry: false,
    },
  )

  return (
    <div>
      <H4>{t('listingDetails.near', { location: addressFormatter.printStreetInformation(listing.address) })}</H4>
      {isLoading ? (
        <Spinner />
      ) : (
        <Flex gap={2} flexDirection='column' justifyContent='space-between' flexWrap='wrap'>
          <Flex className={classes.mapContainer}>
            <GoogleMap center={center} height={500} width='100%' zoom={15}>
              {subwayList?.length &&
                subwayList.map((subwayStation) => {
                  return <Markers.Subway position={subwayStation.location} key={subwayStation.name} />
                })}
              {universityList?.length &&
                universityList.map((university) => {
                  return <Markers.University position={university.location} key={university.name} />
                })}
              <Markers.Guiker position={center} />
            </GoogleMap>
          </Flex>
          {(!!subwayList?.length || !!universityList?.length) && (
            <Flex flexDirection='column' gap={2}>
              {!!subwayList?.length && (
                <PointOfInterestContainer pointOfInterests={subwayList} type={PointOfInterestType.SUBWAY_STATION} />
              )}
              {!!universityList?.length && (
                <PointOfInterestContainer pointOfInterests={universityList} type={PointOfInterestType.UNIVERSITY} />
              )}
            </Flex>
          )}
        </Flex>
      )}
    </div>
  )
}

export { Location }
